import { useLayoutEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import "./Listing.scss";
import "src/app/pages/SmartDP/Search/SDPSearchForm/SmartDPSearchForm.scss";
import ListingHeaderContainer from "app/pages/Listing/ListingHeader/ListingHeaderContainer";
import { PRODUCT_EXPERIENCES } from "app/constants";
import { scrollToElement } from "app/utils/scroller";
import { forceCheck } from "react-lazyload";
import CookiePolicyFooterContainer from "app/pages/.shared/CookiePolicyFooter/CookiePolicyFooterContainer";
import ListingContentContainer from "app/pages/Listing/ListingContent/ListingContentContainer";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import PartnerSEO from "app/pages/.shared/PartnerSEO/PartnerSEO";
import ListingHeadMetadata from "app/pages/Listing/ListingHeadMetadata";
import ScrollToTopButton from "app/pages/.shared/ScrollToTopButton/ScrollToTopButton";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { useIntl } from "react-intl";
import {
	FilterProvider,
	FilterTrigger,
	FilterDrawer,
	StickyFilterButtonTrigger,
	OPTION_MODE,
	FilterSortBar,
} from "app/features/product/productFilters";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import { useIntersectionObserver } from "app/hooks";
import classNames from "classnames";
import { mergeRefs } from "react-merge-refs";

const Listing = ({ idSpotlight, displayProductsCount, listingSEO = {}, resolution, products }) => {
	const { track } = useAnalytics();
	const listingHeaderRef = useRef();
	const topOfProductListRef = useRef();
	const { formatDate, formatMessage } = useIntl();
	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

	const [sentinelRef, isIntersecting] = useIntersectionObserver({
		rootMargin: "1px 0px 0px 0px",
	});

	const filterSortBarClassName = classNames("listing__filter-sort-bar-background", {
		"listing__filter-sort-bar-background--stuck": !isIntersecting,
	});

	useLayoutEffect(() => {
		// force l'affichage des images des produits sur mobile lorsqu'un filtre est actif
		forceCheck();
	}, []);

	const scrollToListingHeader = () => {
		scrollToElement({
			element: listingHeaderRef.current,
			offset: 0,
			disableSmoothScroll: true,
		});
	};

	const shouldShowMultiStageJourneyOption = products?.some(product =>
		[
			PRODUCT_EXPERIENCES.CIRCUIT,
			PRODUCT_EXPERIENCES.AUTOTOUR,
			PRODUCT_EXPERIENCES.CRUISE,
		].includes(product?.productExperience)
	);

	const facetConfigs = useMemo(
		() => [
			{
				field: "destinations",
				title: formatMessage({ id: "product.page.filter.destinations.label" }),
				valueGetter: item => item.country,
				...(shouldShowMultiStageJourneyOption && {
					buildOptions: () => {
						return [
							{
								value: "multi-stage-journey",
								label: formatMessage({
									id: "listing.filter.destination.multi.destinations.label",
								}),
							},
						];
					},
					optionMode: OPTION_MODE.MERGE,
					customMatcher: product => {
						return [
							PRODUCT_EXPERIENCES.CIRCUIT,
							PRODUCT_EXPERIENCES.AUTOTOUR,
							PRODUCT_EXPERIENCES.CRUISE,
						].includes(product?.productExperience);
					},
				}),
			},
			{
				field: "themes",
				title: formatMessage({ id: "listing.filter.theme.label" }),
				valueGetter: item => item.topics,
			},
			{
				field: "months",
				title: formatMessage({ id: "listing.filter.month.label" }),
				valueGetter: item => item.months.map(month => month.label),
				formatLabel: ({ label }) => {
					const [year, month] = label.split("/");
					const date = new Date(Number(year), Number(month) - 1, 1);
					return formatDate(date, { month: "long", year: "numeric" });
				},
				customSort: (a, b) => {
					return a?.value?.localeCompare(b?.value);
				},
			},
		],
		[shouldShowMultiStageJourneyOption]
	);

	const handleOpenFilterDrawer = () => {
		track("product-filter-open");
	};

	const handleFilterChange = (facetName, facetValue) => {
		track("pp-filter-facet-clicked", { facetName, facetValue });
	};

	const handleScrollToTop = () => {
		if (!topOfProductListRef.current) {
			return;
		}

		requestAnimationFrame(() => {
			topOfProductListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
		});
	};

	return (
		<FilterProvider facetConfigs={facetConfigs} products={products}>
			<div className="listing">
				<ListingHeadMetadata />
				<ListingHeaderContainer
					idSpotlight={idSpotlight}
					scrollToListingHeader={scrollToListingHeader}
					listingHeaderRef={listingHeaderRef}
					displayProductsCount={displayProductsCount}
				/>

				<div ref={mergeRefs([sentinelRef, topOfProductListRef])} aria-hidden="true" />

				{isMobile ? (
					<>
						<FilterTrigger asChild onClick={handleOpenFilterDrawer}>
							<StickyFilterButtonTrigger />
						</FilterTrigger>

						<FilterDrawer
							isSinglePanelMode
							onChange={handleFilterChange}
							onSubmit={handleScrollToTop}
						/>
					</>
				) : (
					<div className={filterSortBarClassName}>
						<FilterSortBar
							isSortAvailable={false}
							className="listing__filter-sort-bar"
							onSubmit={handleScrollToTop}
						/>
					</div>
				)}

				<ListingContentContainer
					displayProductsCount={displayProductsCount}
					spotlightUri={idSpotlight}
				/>

				<PartnerSEO
					title={listingSEO.title}
					description={listingSEO.description}
					resolution={resolution}
				/>

				<ScrollToTopButton />

				<CookiePolicyFooterContainer />
			</div>
			<PushNotificationContainer />
		</FilterProvider>
	);
};

Listing.propTypes = {
	idSpotlight: PropTypes.string,
	listingSEO: PropTypes.object,
	displayProductsCount: PropTypes.bool,
	resolution: PropTypes.string,
	products: PropTypes.arrayOf(PropTypes.object),
};

export default Listing;
